import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Loader2, Mic, MicOff, Plus, Save, Trash2, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { appendVisitVoiceUpdate, processVoiceInput } from '../api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const emptyResult = {
  recording_id: '',
  transcription: '',
  discussion_notes: '',
  samples_shown: '',
  potential_items: [],
  follow_up_tasks: [],
  audio_url: '',
};

export const VisitVoiceUpdateDialog = ({ buyerVisit, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [result, setResult] = useState(emptyResult);
  const [previewUrl, setPreviewUrl] = useState('');
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!recording) return undefined;
    const timer = window.setInterval(() => setSeconds((current) => current + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const resetDialog = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRecording(false);
    setProcessing(false);
    setSaving(false);
    setSeconds(0);
    setPreviewUrl('');
    setResult(emptyResult);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && (recording || processing || saving)) return;
    setOpen(nextOpen);
    if (!nextOpen) resetDialog();
  };

  const processRecording = async (blob) => {
    setPreviewUrl(URL.createObjectURL(blob));
    setProcessing(true);
    try {
      const response = await processVoiceInput(blob, buyerVisit.buyer_name, {
        buyerVisitId: buyerVisit.id,
      });
      const data = response.data;
      setResult({
        recording_id: data.recording_id,
        transcription: data.transcription || '',
        discussion_notes: data.discussion_notes || '',
        samples_shown: data.samples_shown || '',
        potential_items: data.potential_items || [],
        follow_up_tasks: (data.follow_up_tasks || []).map((task) => ({
          description: task.description || '',
          due_date: task.due_date || '',
          assigned_to: [],
        })),
        audio_url: data.audio_url || '',
      });
      toast.success('Update transcribed. Review before saving.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process voice update');
    } finally {
      setProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const preferredType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
        .find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        processRecording(blob);
      };
      setSeconds(0);
      recorder.start();
      setRecording(true);
    } catch (error) {
      toast.error('Microphone access is required to add a voice update');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
  };

  const updatePotentialItem = (index, field, value) => {
    setResult((current) => ({
      ...current,
      potential_items: current.potential_items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }));
  };

  const updateTask = (index, field, value) => {
    setResult((current) => ({
      ...current,
      follow_up_tasks: current.follow_up_tasks.map((task, taskIndex) => (
        taskIndex === index ? { ...task, [field]: value } : task
      )),
    }));
  };

  const saveUpdate = async () => {
    const invalidTask = result.follow_up_tasks.find((task) => !task.description.trim() || !task.due_date);
    if (invalidTask) {
      toast.error('Each follow-up task needs a description and due date');
      return;
    }
    setSaving(true);
    try {
      const response = await appendVisitVoiceUpdate(buyerVisit.id, {
        recording_id: result.recording_id,
        transcription: result.transcription,
        discussion_notes: result.discussion_notes,
        samples_shown: result.samples_shown,
        potential_items: result.potential_items.filter((item) => item.item_description?.trim()),
        follow_up_tasks: result.follow_up_tasks,
      });
      onSaved(response.data);
      toast.success('Voice update added without changing the original visit');
      setOpen(false);
      resetDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save voice update');
    } finally {
      setSaving(false);
    }
  };

  const formattedTime = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <>
      <Button type="button" size="sm" className="min-h-[44px] sm:min-h-9 bg-maroon-700 hover:bg-maroon-800" onClick={() => setOpen(true)} data-testid={`add-voice-update-${buyerVisit.id}`}>
        <Plus className="mr-1 h-4 w-4" /> Add Update (Voice)
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid={`voice-update-dialog-${buyerVisit.id}`}>
          <DialogHeader>
            <DialogTitle>Add voice update — {buyerVisit.buyer_name}</DialogTitle>
            <DialogDescription>The original visit date, notes, and recording will remain unchanged.</DialogDescription>
          </DialogHeader>

          {!result.recording_id && !processing && (
            <div className="py-8 text-center">
              {!recording ? (
                <Button type="button" onClick={startRecording} className="h-14 bg-maroon-700 hover:bg-maroon-800" data-testid={`start-voice-update-${buyerVisit.id}`}>
                  <Mic className="mr-2 h-6 w-6" strokeWidth={2.8} /> Start Recording
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="text-2xl font-mono text-red-600" data-testid={`voice-update-timer-${buyerVisit.id}`}>{formattedTime}</div>
                  <Button type="button" onClick={stopRecording} className="h-14 bg-red-600 hover:bg-red-700 animate-pulse" data-testid={`stop-voice-update-${buyerVisit.id}`}>
                    <MicOff className="mr-2 h-6 w-6" /> Stop & Process
                  </Button>
                </div>
              )}
            </div>
          )}

          {processing && (
            <div className="flex flex-col items-center justify-center gap-3 py-10" data-testid={`processing-voice-update-${buyerVisit.id}`}>
              <Loader2 className="h-8 w-8 animate-spin text-maroon-700" />
              <p className="text-sm text-gray-600">Transcribing only this new recording…</p>
            </div>
          )}

          {result.recording_id && !processing && (
            <div className="space-y-5" data-testid={`voice-update-review-${buyerVisit.id}`}>
              {previewUrl && (
                <div>
                  <Label className="flex items-center gap-1 text-xs"><Volume2 className="h-3.5 w-3.5" />New recording</Label>
                  <audio controls src={previewUrl} className="mt-1 w-full" data-testid={`voice-update-preview-${buyerVisit.id}`} />
                </div>
              )}
              <div>
                <Label htmlFor={`update-transcript-${buyerVisit.id}`}>Transcription</Label>
                <Textarea id={`update-transcript-${buyerVisit.id}`} value={result.transcription} onChange={(event) => setResult((current) => ({ ...current, transcription: event.target.value }))} rows={3} data-testid={`voice-update-transcription-${buyerVisit.id}`} />
              </div>
              <div>
                <Label htmlFor={`update-notes-${buyerVisit.id}`}>New Discussion Notes</Label>
                <Textarea id={`update-notes-${buyerVisit.id}`} value={result.discussion_notes} onChange={(event) => setResult((current) => ({ ...current, discussion_notes: event.target.value }))} rows={3} data-testid={`voice-update-notes-${buyerVisit.id}`} />
              </div>
              <div>
                <Label htmlFor={`update-samples-${buyerVisit.id}`}>New Samples / Items Shown</Label>
                <Textarea id={`update-samples-${buyerVisit.id}`} value={result.samples_shown} onChange={(event) => setResult((current) => ({ ...current, samples_shown: event.target.value }))} rows={2} data-testid={`voice-update-samples-${buyerVisit.id}`} />
              </div>

              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>New Potential Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setResult((current) => ({ ...current, potential_items: [...current.potential_items, { item_description: '', notes: '' }] }))} data-testid={`add-update-item-${buyerVisit.id}`}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add Item
                  </Button>
                </div>
                {result.potential_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 rounded border border-gray-200 p-2">
                    <Input value={item.item_description || ''} onChange={(event) => updatePotentialItem(index, 'item_description', event.target.value)} placeholder="Item" data-testid={`update-item-description-${buyerVisit.id}-${index}`} />
                    <Input value={item.notes || ''} onChange={(event) => updatePotentialItem(index, 'notes', event.target.value)} placeholder="Notes" data-testid={`update-item-notes-${buyerVisit.id}-${index}`} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setResult((current) => ({ ...current, potential_items: current.potential_items.filter((_, itemIndex) => itemIndex !== index) }))} data-testid={`remove-update-item-${buyerVisit.id}-${index}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </section>

              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Follow-up Tasks</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setResult((current) => ({ ...current, follow_up_tasks: [...current.follow_up_tasks, { description: '', due_date: '', assigned_to: [] }] }))} data-testid={`add-update-task-${buyerVisit.id}`}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add Task
                  </Button>
                </div>
                {result.follow_up_tasks.map((task, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_11rem_auto] gap-2 rounded border border-gray-200 p-2">
                    <Input value={task.description} onChange={(event) => updateTask(index, 'description', event.target.value)} placeholder="Task description" data-testid={`update-task-description-${buyerVisit.id}-${index}`} />
                    <div className="relative">
                      <Calendar className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <Input type="date" value={task.due_date} onChange={(event) => updateTask(index, 'due_date', event.target.value)} className="pl-8" data-testid={`update-task-due-${buyerVisit.id}-${index}`} />
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setResult((current) => ({ ...current, follow_up_tasks: current.follow_up_tasks.filter((_, taskIndex) => taskIndex !== index) }))} data-testid={`remove-update-task-${buyerVisit.id}-${index}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </section>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={recording || processing || saving} data-testid={`cancel-voice-update-${buyerVisit.id}`}>Cancel</Button>
            {result.recording_id && !processing && (
              <Button type="button" onClick={saveUpdate} disabled={saving} className="bg-maroon-700 hover:bg-maroon-800" data-testid={`save-voice-update-${buyerVisit.id}`}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Confirm & Append
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};